import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { SectionHeader } from "./SectionHeader";
import { GALLERY } from "@/lib/siteContent";
import { useT } from "@/lib/i18n";

// Whatever the photo count, the final row would otherwise end in a hole: 29
// photos leave 3 empty slots in a four-column row. The last photo takes those
// slots instead and turns into a wide closing shot. The classes are written out
// in full because Tailwind only keeps class names it can see in the source.
const FILL_LAST = {
  2: { 1: "", 2: "col-span-2 aspect-[16/9]" },
  3: { 1: "", 2: "md:col-span-2 md:aspect-[16/9]", 3: "md:col-span-3 md:aspect-[16/7]" },
  4: { 1: "", 2: "lg:col-span-2 lg:aspect-[16/9]", 3: "lg:col-span-3 lg:aspect-[16/6]", 4: "lg:col-span-4 lg:aspect-[16/5]" },
};

/** Columns the last item must span so the closing row comes out flush. */
const spanToFill = (count, cols) => (count % cols === 0 ? 1 : cols - (count % cols) + 1);

export const Gallery = () => {
  const { t } = useT();
  const [active, setActive] = useState(null);
  const lastFill = [
    FILL_LAST[2][spanToFill(GALLERY.length, 2)],
    FILL_LAST[3][spanToFill(GALLERY.length, 3)],
    FILL_LAST[4][spanToFill(GALLERY.length, 4)],
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => { if (e.key === "Escape") setActive(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <section id="galeria" className="section-y bg-secondary/40" aria-labelledby="galeria-title">
      <div className="container-x">
        <SectionHeader kicker={t.gallery.kicker} title={t.gallery.title} subtitle={t.gallery.subtitle} />

        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
          {GALLERY.map((photo, i) => {
            const label = t.gallery.categories[photo.cat] ?? "";
            return (
              <button
                key={i}
                type="button"
                onClick={() => setActive(i)}
                className={`group relative aspect-[4/3] overflow-hidden rounded-xl border border-border bg-card shadow-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  i === GALLERY.length - 1 ? lastFill : ""
                }`}
                data-testid={`gallery-thumb-${i}`}
                aria-label={t.gallery.openAlt(i + 1, GALLERY.length)}
              >
                <img
                  src={photo.src}
                  alt={t.gallery.photoAlt(label, i + 1)}
                  loading="lazy"
                  width="900"
                  height="675"
                  className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]"
                />
                <span className="pointer-events-none absolute left-2 bottom-2 rounded-full bg-white/90 backdrop-blur px-2.5 py-0.5 text-[11px] font-medium text-foreground shadow-soft">
                  {label}
                </span>
                <span className="pointer-events-none absolute inset-0 bg-foreground/0 group-hover:bg-foreground/10 transition-colors duration-200" />
              </button>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <div className="absolute inset-0" style={{ background: "hsl(24 18% 6% / 0.85)" }} />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setActive(null); }}
              className="absolute top-5 right-5 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-foreground shadow-lifted hover:bg-white"
              aria-label={t.gallery.close}
              data-testid="gallery-close"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.img
              key={active}
              src={GALLERY[active].src}
              alt={t.gallery.photoAlt(t.gallery.categories[GALLERY[active].cat] ?? "", active + 1)}
              className="relative z-[1] max-h-[86vh] max-w-[92vw] rounded-lg object-contain shadow-lifted"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
